import { useMemo } from 'react';
import { App } from 'antd';
import { trpc } from '../../lib/trpc';
import InvitationManagement, {
  InvitationListItem,
  InvitationManagementProps,
} from './InvitationManagement';

interface InvitationManagementContainerProps {
  companyId: string;
  showBulkUpload?: boolean;
}

export const InvitationManagementContainer: React.FC<InvitationManagementContainerProps> = ({
  companyId,
  showBulkUpload = true,
}) => {
  const { message } = App.useApp();
  const listQuery = trpc.user.invitations.list.useQuery(
    {
      companyId,
      page: 1,
      limit: 50,
    },
    {
      enabled: Boolean(companyId),
    },
  );

  const resendMutation = trpc.user.invitations.resend.useMutation({
    onSuccess: () => {
      message.success('招待メールを再送信しました');
      listQuery.refetch();
    },
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : '再送信に失敗しました';
      message.error(errorMessage);
    },
  });

  const cancelMutation = trpc.user.invitations.cancel.useMutation({
    onSuccess: () => {
      message.success('招待を取り消しました');
      listQuery.refetch();
    },
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : '取り消しに失敗しました';
      message.error(errorMessage);
    },
  });

  const invitations: InvitationListItem[] = useMemo(() => {
    return (listQuery.data?.invitations ?? []).map((invitation) => ({
      id: invitation.id,
      email: invitation.email,
      name: invitation.name,
      role: invitation.role,
      inviterName: invitation.inviterName,
      inviterEmail: invitation.inviterEmail,
      invitedBy: invitation.invitedBy,
      status: invitation.status,
      createdAt: invitation.createdAt,
      expiresAt: invitation.expiresAt,
      acceptedAt: invitation.acceptedAt,
      canceledAt: invitation.canceledAt,
      message: invitation.message,
    }));
  }, [listQuery.data]);

  const managementProps: InvitationManagementProps = {
    invitations,
    loading: listQuery.isLoading || listQuery.isRefetching,
    onResend: async (invitationId: string) => {
      await resendMutation.mutateAsync({ invitationId, companyId });
    },
    onCancel: async (invitationId: string) => {
      await cancelMutation.mutateAsync({ invitationId, companyId });
    },
    onRefresh: () => listQuery.refetch(),
    showBulkUpload,
    companyId,
  };

  return <InvitationManagement {...managementProps} />;
};

export default InvitationManagementContainer;
